import {compact} from 'lodash'

export const idField = 'id';
export const parentIdField = 'parent_id';
export const childrenField = 'children';

const mergeObject = (obj, el) => ({ ...obj, ...el });

export const getFlattenChildren = (tree) => (children = []) => {
  if(!tree || !tree[childrenField] || tree[childrenField].length === 0) {
    return children;
  }
  return getFlattenChildren(tree[childrenField], tree[childrenField].map(({slug}) => slug))([...tree[childrenField].map(({slug}) => slug), ...children])
};

export const flattenTree = (tree, key=idField) => {
  if(!tree) return {};
  if(!tree[childrenField] || tree[childrenField].length === 0) {
    return {
      [tree[key]]: tree,
    };
  }
  return {
    [tree[key]]: { ...tree, [childrenField]: tree[childrenField].map(child => child[key]) },
    ...(tree[childrenField].map(child => flattenTree(child, key)).reduce(mergeObject, {})),
  };
};

export const getParentage = (tree, parents = []) => {
  if(!tree) return {};
  if(!tree[childrenField] || tree[childrenField].length === 0) {
    return {
      [tree[idField]]: parents,
    };
  }
  return {
    [tree[idField]]: parents,
    ...(tree[childrenField].map(child => getParentage(child, [ ...parents, tree[idField] ])).reduce(mergeObject, {})),
  };
};

// Finds the top-most left-most element in a tree that matches predicate
// Based on https://stackoverflow.com/a/9133690
export const findTree = (tree = [], f) => {
  let stack = [], node;
  stack.push(...tree);

  while (stack.length > 0) {
    node = stack.pop();
    if (f(node)) {
      return node;
    } else if(node[childrenField] && node[childrenField].length) {
      stack.push(...node[childrenField]);
    }
  }

  return null;
}

// Map over tree a function taking (node, processedParent)
export const mapOverTree = (tree = [], childrenKey = 'children') => (f, processedParent = null) => {
  return tree.map(node => {
    const processed = f(node, processedParent);

    if((node[childrenKey] || []).length === 0) {
      return processed;
    }

    return {
      ...processed,
      [childrenKey]: mapOverTree(node[childrenKey], childrenKey)(f, processed),
    }
  });
};

// Map over tree a function taking (node, processedChildren)
// REMEMBER: When returning each f(node, children) you NEED to return the children like:
//           return {
//             ...node,
//             [WHATEVER],
//             children,
//           }
//
//           Otherwise the children prop get overritten by the one in 'node'
export const mapOverTreeFromLeafs = (tree = [], childrenKey = 'children') => (f) => {
  return compact(tree.map(node => {
    if((node[childrenKey] || []).length === 0) {
      return f(node, []);
    }

    return f(node, mapOverTreeFromLeafs(node[childrenKey], childrenKey)(f));
  }));
};

// Reduce over tree a function taking (acc, node)
export const reduceOverTree = (tree = [], childrenKey = 'children') => (f, initialValue) => {
  return tree.reduce((acc, node) => {
    const processed = f(acc, node);

    if((node[childrenKey] || []).length === 0) {
      return processed;
    }

    return reduceOverTree(node[childrenKey], childrenKey)(f, processed);
  }, initialValue);
};

// Map over tree a function taking (node, processedParent)
export const appendLeafToTree = (tree = [], childrenKey = 'children') => (f, leaf) => {
  return tree.map(node => {
    const match = f(node);

    if((node[childrenKey] || []).length === 0) {
      return match
        ? {
          ...node,
          [childrenKey]: [ leaf ],
        }
        : node;
    }

    return match
    ? {
        ...node,
        [childrenKey]: (appendLeafToTree(node[childrenKey], childrenKey)(f, leaf)).concat(leaf),
      }
    : {
        ...node,
        [childrenKey]: appendLeafToTree(node[childrenKey], childrenKey)(f, leaf),
      }
  });
};

export const searchTreeWithFullPath = (
  tree = [], f, childrenKey = 'children'
) => {
  return tree.reduce(function iter(r, a) {
    let children;
    if (Array.isArray(a[childrenKey])) {
      children = a[childrenKey].reduce(iter, []);
    }
    const passesSearchCondition = f(a);
    if (children.length || passesSearchCondition) {
      return r.concat({ ...a, children, matched: passesSearchCondition });
    }
    return r;
  }, []);
};
